<template>
  <div
    class="menu-drawer critical-component-hide"
    :class="`show-tier${currentTier}`"
  >
    <div
      ref="menuDrawer"
      class="menu-drawer__navigation"
    >
      <!-- Top Features -->
      <div
        v-if="drawerTopFeatures?.length > 0"
        class="menu-drawer__top-features"
      >
        <swiper
          :modules="modules"
          :slides-per-view="2.3"
          :space-between="8"
        >
          <swiper-slide
            v-for="(feature, index) of drawerTopFeatures"
            :id="`menu-top-feature-${index}-${feature.id}`"
            :key="`menu-top-feature-${index}-${feature.id}`"
            class="menu-drawer__top-features-slide"
          >
            <div>
              <a
                class="menu-drawer__top-features-link"
                :href="feature.settings.feat_url"
                :target="getTargetForUrl(feature.settings.feat_url)"
              >
                <responsive-image
                  v-if="feature.settings.feature_image"
                  :alt="feature.settings.feat_title"
                  :image="{src: feature.settings.feature_image}"
                  :ratio="'200:200'"
                  swiper-lazy-load
                />
                <p class="menu-drawer__top-features-title">{{ feature.settings.feat_title }}</p>
              </a>
            </div>
          </swiper-slide>
        </swiper>
      </div>
      <!-- END Top Features -->
      <nav class="menu-drawer__menus">
        <!-- Tier 1 -->
        <ul
          class="menu-drawer__menu menu-drawer__tier1"
          :class="{ 'is-active': getActiveState(false) }"
        >
          <li
            v-if="$slots.evergreenAnnouncement"
            :ref="(args) => setItemRefs(args, { tier: 1, type: 'back' })"
            class="menu-drawer__menu-item menu-drawer__evergreen"
          >
            <slot name="evergreenAnnouncement" />
          </li>

          <!-- <template v-if="schools">
            <li
              class="menu-drawer__menu-item"
            >
              <button
                class="menu-drawer__link menu-drawer__button h6"
              ></button>
            </li>
          </template> -->
          <li
            v-for="(tier1, tier1Index) in computedNavigation"
            :ref="(args) => setItemRefs(args, { tier: 1, type: 'link' })"
            :key="tier1.handle"
            class="menu-drawer__menu-item"
          >
            <button
              v-if="tier1.links?.length"
              class="menu-drawer__link menu-drawer__button h6"
              :tabindex="getActiveState(false) ? 0 : -1"
              :aria-label="`${$string('accessibility.open')} ${tier1.title}`"
              :class="{
                [`menu-drawer__link--${tier1.options?.color}`]: tier1.options?.color
              }"
              @click="handleLinkClick(tier1, tier1Index)"
              v-html="`${tier1.title} ${$icon('direction.arrow.right')}`"
            />

            <a
              v-else
              class="menu-drawer__link h6"
              :href="tier1.url.includes('visualizer') ? tier1.url + '/headboard' : tier1.url"
              :target="getTargetForUrl(tier1.url)"
              :tabindex="getActiveState(false) ? 0 : -1"
              :class="{
                [`menu-drawer__link--${tier1.options?.color}`]: tier1.options?.color
              }"
            >
              <span
                v-if="tier1.options?.image"
                class="icon"
              >
                <img
                  ref="navIcon"
                  :alt="tier1.title + ' ' + $string('accessibility.icon')"
                  :src="tier1.options.image_hover"
                >
              </span>
              <span>
                {{ tier1.title }}
              </span>
            </a>

            <!-- Tier 2 -->
            <ul
              v-if="tier1.links?.length"
              class="menu-drawer__menu menu-drawer__tier2"
              :class="{ 'is-active': getActiveState(tier1) || getChildActiveState(tier1) }"
            >
              <li class="menu-drawer__menu-item">
                <button
                  :ref="(args) => setItemRefs(args, {
                    handle: tier1.handle,
                    tier: 2,
                    type: 'back',
                  })"
                  class="
                    menu-drawer__link
                    menu-drawer__button
                    menu-drawer__back
                    h6
                  "
                  :class="{
                    [`menu-drawer__link--${tier1.options?.color}`]: tier1.options?.color
                  }"
                  :aria-label="`${$string('general.menu_drawer.back_to')} ${$string('general.menu_drawer.top_level')}`"
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  @click="handleLinkClick(false)"
                  v-html="`${$icon('direction.arrow.left')} ${tier1.title}`"
                />
              </li>

              <li
                v-for="(tier2) in tier1.links"
                :ref="(args) => setItemRefs(args, {
                  handle: tier1.handle,
                  tier: 2,
                  type: 'link',
                })"
                :key="tier2.handle"
                class="menu-drawer__menu-item"
              >
                <button
                  v-if="tier2.links?.length"
                  class="menu-drawer__link menu-drawer__button body-1"
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  :aria-label="`${$string('accessibility.open')} ${tier2.title}`"
                  :class="{
                    [`menu-drawer__link--${tier2.options?.color}`]: tier2.options?.color
                  }"
                  @click="toggleMenuAccordion($event, tier2.handle)"
                >
                  {{ tier2.title }}
                  <span
                    :ref="args => accordionIcons[tier2.handle] = args"
                    v-html="$icon('misc.plus')"
                  />
                </button>

                <a
                  v-else
                  class="menu-drawer__link body-1"
                  :href="tier2.url"
                  :target="getTargetForUrl(tier2.url)"
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  v-text="tier2.title"
                />

                <!-- Tier 3 -->
                <ul
                  v-if="tier2.links?.length"
                  ref="menuDrawerAccordion"
                  class="menu-drawer__menu menu-drawer__tier3"
                  :class="{ 'is-active': getActiveState(tier2) }"
                >
                  <li
                    v-for="(tier3, index) in tier2.links"
                    :ref="(args) => setItemRefs(args, {
                      handle: tier2.handle,
                      tier: 3,
                      type: 'link',
                    })"
                    :key="tier3.handle"
                    class="menu-drawer__menu-item"
                    @mouseenter="handleIconHover($event, tier3, navIcons[index])"
                    @mouseleave="handleIconHover($event, tier3, navIcons[index])"
                    @touchstart="handleIconHover($event, tier3, navIcons[index])"
                  >
                    <a
                      class="menu-drawer__link body-1"
                      :class="{
                        [`menu-drawer__link--${tier3.options?.color}`]: tier3.options?.color,
                        [`${tier3.options?.extraClass}`]: tier3.options?.extraClass,
                      }"
                      :href="tier3.url"
                      :target="getTargetForUrl(tier3.url)"
                      :tabindex="getActiveState(tier2) ? 0 : -1"
                      @click="onTier3Click()"
                    >
                      <span
                        v-if="tier3.options?.image && (tier2.title.toLowerCase().includes('featured') || tier1.handle.toLowerCase() === 'schools')"
                        class="icon"
                      >
                        <img
                          :ref="el => navIcons[index] = el"
                          :alt="tier3.title + ' ' + $string('accessibility.icon')"
                          :src="getIcon(tier3)"
                        >
                      </span>
                      {{ tier3.title }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>

      </nav>
      <!-- Account link -->
      <div
        :ref="(args) => setItemRefs(args, { tier: 1, type: 'link' })"
        class="menu-drawer__account"
      >
        <a
          v-for="(footerLink, idx) in footerLinks"
          :key="`footer-link-${idx}-${footerLink.id}`"
          :href="footerLink.settings.drawer_footer_link_url"
          class="menu-drawer__link body-1"
          :tabindex="getActiveState(false) ? 0 : -1"
        >
          <img
            v-if="footerLink.settings.drawer_footer_link_image"
            :src="footerLink.settings.drawer_footer_link_image"
          >
          <span
            v-else-if="footerLink.settings.drawer_footer_link_icon"
            v-html="$icon(footerLink.settings.drawer_footer_link_icon)"
          />
          <template
            v-if="$variable('customer.loggedIn') && footerLink.settings.drawer_footer_link_logged_in_title"
          >
            {{ footerLink.settings.drawer_footer_link_logged_in_title }}
          </template>
          <template v-else>
            {{ footerLink.settings.drawer_footer_link_title }}
          </template>
        </a>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Menu drawer
 * -----------------------------------------------------------------------------
 * Mobile menu drawer.
 * - CSS variable --menu-drawer-height is updated to match height of current
 *   visible links.
 *
 * @param {Array} navigation - Mobile linklist.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y } from 'swiper'
import HeaderBanner from '~async/header-banner/header-banner'
import timings from '~/config/timings'
import classes from '~/config/classes'
import { getIcon, handleIconHover } from '~/helpers/general'

const ALPHABET = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z"];

export default {
  name: 'MenuDrawer',

  components: {
    HeaderBanner,
    Swiper,
    SwiperSlide,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    navigation: {
      type: Array,
      default: () => ([]),
    },
    tierTwoFeatures: {
      type: Object,
      default: () => ({}),
    },
    drawerTopFeatures: {
      type: Array,
      default: () => [],
    },
    footerLinks: {
      type: Array,
      default: () => [],
    },
    schools: {
      type: Array,
      default: () => [],
    },
    schoolsLabel: {
      type: String,
      default: 'Schools',
    },
    schoolsPosition: {
      type: Number,
      default: 0,
    },
    schoolsNameCol: {
      type: String,
      default: '',
    },
    schoolsLetterCol: {
      type: String,
      default: '',
    },
    schoolsLogoCol: {
      type: String,
      default: '',
    },
    schoolsLinkCol: {
      type: String,
      default: '',
    },
    schoolsNotFoundLabel: {
      type: String,
      required: false,
      default: "",
    },
    schoolsNotFoundLink: {
      type: String,
      required: false,
      default: "",
    },
  },

  setup(props) {
    return {
      modules: [A11y],
    }
  },

  data() {
    return {
      accordionIcons: {},
      currentLink: false,
      currentHeight: false,
      getIcon,
      handleIconHover,
      itemRefs: [],
      navIcons: [],
      alphabet: ALPHABET,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute current item refs in current link.
     * @returns {Array}
     */
    currentItemRefs() {
      if (this.currentLink) {
        return this.itemRefs.filter((itemRef) => {
          return itemRef.handle === this.currentLink.handle
        })
      }

      return this.itemRefs.filter((itemRef) => itemRef.tier === 1)
    },

    /**
     * Compute current tier to show.
     * @returns {Number}
     */
    currentTier() {
      if (!this.currentLink) {
        return 1
      }

      return this.currentLink.tier + 1
    },

    /**
     * Returns object with schools grouped by Letter
     */
    schoolByLetter() {
      if (!this.schools) return ({})

      const schoolsDict = this.schools.reduce((acc, s) => {
        const letter = s[this.schoolsLetterCol].toLowerCase()
        if (!acc[letter]) {
          acc[letter] = []
        }
        acc[letter].push(s)
        return acc
      }, {})

      const dict = {}
      for (let i = 4; i < this.alphabet.length; i = Math.min(i + 4, this.alphabet.length)) {
        const letters = this.alphabet.slice(i - 4, i)
        const firstLetter = letters[0]
        const lastLetter = letters[letters.length - 1]

        const schoolsArr = letters.map(l => schoolsDict[l])
        dict[`${firstLetter} - ${lastLetter}`] = schoolsArr
      }

      return dict
    },

    /**
     * Computed navigation. Used to add schools to setup navigation
     */
    computedNavigation() {
      const schoolNav = this.schools ? [
        {
          handle: 'schools',
          title: this.schoolsLabel,
          tier: 1,
          links: Object.entries(this.schoolByLetter)
              .map(([letters, schools]) => ({
                handle: `schools-${letters}`,
                title: letters.toUpperCase(),
                tier: 2,
                links: schools?.map((sArr, idx) => sArr?.length ? [
                  {
                    tier: 3,
                    handle: `schools-divider-${sArr[0]?.[this.schoolsLetterCol]}`,
                    title: sArr[0]?.[this.schoolsLetterCol],
                    url: null,
                    options: {
                      extraClass: 'menu-drawer__school-divider',
                    },
                  },
                  ...sArr.map(s =>({
                    tier: 3,
                    handle: `schools-${s[this.schoolsNameCol]}`,
                    title: s[this.schoolsNameCol],
                    url: s[this.schoolsLinkCol]?.length ? s[this.schoolsLinkCol] : null,
                    options: {
                      image: s[this.schoolsLogoCol],
                    },
                  })),
                  ...(idx === 3 && this.schoolsNotFoundLink?.length ? [
                    {
                      tier: 3,
                      handle: `schools-not-found`,
                      title: this.schoolsNotFoundLabel,
                      url: this.schoolsNotFoundLink,
                    },
                  ] : []),
                ] : []).flat() ?? [],
              }))
          ,
        },
      ] : []

      const navigation = [
        ...this.navigation.slice(0, this.schoolsPosition),
        ...schoolNav,
        ...this.navigation.slice(this.schoolsPosition),
      ]
      return navigation
    },
  },

  watch: {

    /**
     * Watch current link to update focus.
     */
    currentLink() {
      this.updateHeight()
    },

    /**
     * Watch active overlay to reset current link.
     */
    getActiveOverlay() {
      this.currentLink = false
    },

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay('menuDrawer')
    },
  },

  mounted() {
    this.updateFocus()

    /**
     * Wait for drawer animation to finish before calculating heights.
     */
    setTimeout(() => {
      this.updateHeight()
    }, timings.m)
  },

  beforeUpdate() {
    this.itemRefs = []
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Get active state for nested menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getActiveState(link) {
      return this.isActive && link.handle === this.currentLink.handle
    },

    /**
     * Get active state of current nested menu's child menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getChildActiveState(link) {
      const tier3LinkActive = link.links.find((tier3Link) => {
        return tier3Link.handle === this.currentLink.handle
      })

      return Boolean(tier3LinkActive)
    },

    /**
     * Set item refs.
     * @param {HTMLElement} element - Item in v-for.
     * @param {String} data.handle - Handle of item.
     * @param {Number} data.tier - Tier of item.
     * @param {String} data.type - Type of item, `link` or `back`.
     */
    setItemRefs(element, data) {
      if (!element) {
        return
      }

      this.itemRefs.push({
        ...data,
        element,
      })
    },

    /**
     * Toggle the tier 2 accordions and update the plus/minus icons.
     * @param {Object} event - The click event.
     * @param {String} handle - The handle of the tier 2 item that was clicked.
     */
    toggleMenuAccordion(event, handle) {
      event.target.classList.toggle(classes.open)
      const icon = this.accordionIcons[handle]
      const hasClass = event.target.classList.contains(classes.open)

      if (icon) {
        icon.innerHTML = hasClass ?
          this.$icon('misc.minus') :
          this.$icon('misc.plus')
      }

      this.updateHeight()
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick(() => {
        cnvs.EventBus.emit('overlay:update', 'menuDrawer')
      })
    },

    /**
     * Update height of current active navigation.
     * - Goes through each item in currentItemRefs and get height of element.
     */
    updateHeight() {
      nextTick(() => {
        const height = this.currentItemRefs.reduce((previous, current) => {
          return (
            (typeof previous === 'number' ? previous : previous.element.offsetHeight) +
            current.element.offsetHeight
          )
        })

        /**
         * Set CSS variable.
         */
        document.documentElement.style.setProperty(
          '--menu-drawer-height',
          `${height}px`,
        )

        /**
         * Update Vue data for easier debugging.
         */
        this.currentHeight = height
      })
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     */
    handleLinkClick(link) {
      this.currentLink = link
    },

    getTargetForUrl(url) {
      try {
        const urlObj = new URL(url)
        if (urlObj.hostname.length > 0 && !urlObj.hostname.includes('.dormify.com')) {
          return '_blank'
        }
      } catch (e) {}
      return '_self'
    },

    onTier3Click() {
      this.closeOverlay('menuDrawer')
    },
  },
}
</script>

<style lang="scss">
@import './menu-drawer';
</style>
